import { PERMISSIONS } from "@shared/constants/permissions";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import FormSheet from "@shared/ui/FormSheet";
import { differenceBy } from "lodash";
import React from "react";
import { useIntl } from "react-intl";

import { fieldType } from "#/src/constants/filters/customFieldTypes";
import ContentLoading from "~/components/ContentLoading";
import CustomFieldsAccordion from "~/components/customFields/_customFieldsAccordion";
import UnderlineTabsWrapper from "~/components/UnderlineTabsWrapper";
import PartDocuments from "~/containers/inventory/PartDocuments";
import usePermissions from "~/hooks/usePermissions";
import { getInventoryPart, updateParts } from "~/services";
import { useGetFoldersAccessToken } from "~/services/user/_query";
import { getPartThumbnail, getSortedCustomFields } from "~/utils";

type PartPreviewProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  onClose: () => void;
  isOpen: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customAdditionalFields: any[];
  showCustomFields?: boolean;
  isSharedAssetsPage?: boolean;
};

const PartPreview = ({
  data,
  onClose,
  isOpen,
  customAdditionalFields,
  showCustomFields,
  isSharedAssetsPage = false,
}: PartPreviewProps) => {
  const { inventoryPart, loading } = getInventoryPart(data?._id);

  const { messages } = useIntl();
  const { token: foldersAccessToken } = useGetFoldersAccessToken();
  const { canChange } = usePermissions();
  const { update } = updateParts();

  const allowModification =
    canChange(PERMISSIONS.parts.update, inventoryPart?.createdBy) &&
    !isSharedAssetsPage;
  const [customFieldData, setCustomFieldData] = React.useState({});

  const [customFieldList, setCustomFieldList] = React.useState([]);

  React.useEffect(() => {
    if (inventoryPart && customAdditionalFields?.length > 0) {
      const customFieldsObject = {};
      const customFieldsWithValue =
        inventoryPart?.customFields
          ?.map((item) => {
            if (item?.fieldId?.enabled) {
              customFieldsObject[item?.fieldId?._id] =
                item?.fieldId?.fieldType === fieldType.multiSelect
                  ? item.values
                  : item.value;
              const getOptionValue = item?.fieldId?.options?.find(
                (d) =>
                  d?.value ===
                  (item?.fieldId?.fieldType === fieldType.multiSelect
                    ? item.values
                    : item.value),
              );

              return {
                _id: item?.fieldId?._id,
                label: item?.fieldId?.label,
                type: item?.fieldId?.fieldType,
                fieldType: item?.fieldId?.fieldType,
                isAdditionalField: item?.fieldId?.isAdditionalField,
                slug: item?.fieldId?.slug,
                options: item?.fieldId?.options,
                order: item?.fieldId.order,
                value:
                  item?.fieldId?.fieldType === "singleSelect"
                    ? getOptionValue?.value
                    : item?.value
                    ? item?.value
                    : "",
                values:
                  item?.fieldId?.fieldType === fieldType.multiSelect
                    ? item?.values
                    : [],
                color: getOptionValue?.color || "",
                created_at: customAdditionalFields?.find(
                  (f) => f?._id === item?.fieldId?._id,
                )?.created_at,
              };
            } else return false;
          })
          ?.filter((item) => Boolean(item)) || [];
      setCustomFieldData(customFieldsObject);
      const arrDiff = differenceBy(
        customAdditionalFields,
        customFieldsWithValue,
        "_id",
      );
      setCustomFieldList(
        getSortedCustomFields([...customFieldsWithValue, ...arrDiff]),
      );
    } else if (inventoryPart?.customFields?.length > 0) {
      const customFieldsObject = {};
      inventoryPart.customFields.forEach((field) => {
        customFieldsObject[field._id] = field.value;
      });
      setCustomFieldData(inventoryPart.customFields);
      setCustomFieldList(
        inventoryPart.customFields
          .filter((field) => field.value || field.values)
          .map((field) => ({
            ...field.fieldId,
            value:
              field.fieldId?.fieldType === fieldType.multiSelect
                ? undefined
                : field.value,
            values:
              field.fieldId?.fieldType === fieldType.multiSelect
                ? field.values
                : undefined,
          })),
      );
    }
  }, [inventoryPart, customAdditionalFields]);

  const handleTextFieldSave = (e, textFields, fieldId, updateTextFields) => {
    e.preventDefault();

    const prepareNewCustomFieldData = {
      ...customFieldData,
      [fieldId]: textFields[fieldId],
    };

    const customFieldsArray = [];
    for (const [key, value] of Object.entries(prepareNewCustomFieldData)) {
      if (key && key !== "undefined") {
        customFieldsArray.push({
          fieldId: key,
          ...(customFieldList.find((field) => field?._id === key).fieldType ===
          fieldType.multiSelect
            ? { values: value }
            : { value }),
        });
      }
    }

    console.log("Custom fields array:", customFieldsArray);

    update({
      _id: inventoryPart?._id,
      customFields: customFieldsArray,
    })
      .then(() => {
        delete textFields[fieldId];
        updateTextFields(textFields);
      })
      .catch((err) => {
        console.error(err);
      });
  };
  const handleSelectFieldSave = (fieldId, newValue) => {
    const prepareNewCustomFieldData = {
      ...customFieldData,
      [fieldId]: newValue,
    };

    const customFieldsArray = [];
    for (const [key, value] of Object.entries(prepareNewCustomFieldData)) {
      if (key && key !== "undefined") {
        customFieldsArray.push({
          fieldId: key,
          ...(customFieldList.find((field) => field?._id === key).fieldType ===
          fieldType.multiSelect
            ? { values: value }
            : { value }),
        });
      }
    }

    update({
      _id: inventoryPart?._id,
      customFields: customFieldsArray,
    }).catch((err) => {
      console.error(err);
    });
  };

  return (
    <FormSheet
      open={isOpen}
      onClose={onClose}
      title={data?.name}
      bodyPadding="!px-0 !pt-md !pb-xl"
      hideFooter
    >
      <UnderlineTabsWrapper
        className="pl-2xl"
        useHistoryState={false}
        tabs={[
          {
            id: "part-details",
            label: messages?.inventory["editPartDetailsTitle"],
            content: (
              <>
                <div className="flex flex-col w-full px-2xl gap-2xl">
                  <div className="w-24 h-9xl rounded-md overflow-hidden">
                    <img
                      src={getPartThumbnail(data, true)}
                      className="w-full h-full object-center object-none rounded-xs"
                    />
                  </div>
                  <div className="flex gap-lg w-full justify-evenly">
                    <div className="flex flex-col gap-xs w-6/12 break-words">
                      <BodyText
                        size={BODY_TEXT_SIZES.X_SMALL}
                        color="text-secondary"
                      >
                        {messages?.inventory["labels"].name}
                      </BodyText>
                      <BodyText size={BODY_TEXT_SIZES.X_SMALL}>
                        {data?.name}
                      </BodyText>
                    </div>
                    <div className="flex flex-col gap-xs w-6/12 break-words">
                      <BodyText
                        size={BODY_TEXT_SIZES.X_SMALL}
                        color="text-secondary"
                      >
                        {messages?.inventory["labels"].articleNumber}
                      </BodyText>
                      <BodyText size={BODY_TEXT_SIZES.X_SMALL}>
                        {data?.articleNumber}
                      </BodyText>
                    </div>
                  </div>
                  <div className="flex flex-col gap-xs w-full">
                    <BodyText
                      size={BODY_TEXT_SIZES.X_SMALL}
                      color="text-secondary"
                    >
                      {messages?.inventory["labels"].description}
                    </BodyText>
                    <BodyText size={BODY_TEXT_SIZES.X_SMALL}>
                      {data?.description || "-"}
                    </BodyText>
                  </div>

                  {isSharedAssetsPage ? (
                    loading ? (
                      <div className="w-full flex items-center justify-center">
                        <ContentLoading />
                      </div>
                    ) : (
                      customFieldList.length > 0 && (
                        <CustomFieldsAccordion
                          className="edit-part-custom-fields-accordion"
                          numberFieldDropdownClassName="property-userDropdownMenu-edit-part-number"
                          dateFieldDropdownClassName="property-userDropdownMenu-edit-part-date"
                          textFieldDropdownClassName="property-userDropdownMenu-edit-part-text"
                          selectFieldClassName="edit-part-custom-select-field"
                          customFieldList={customFieldList}
                          //@ts-ignore
                          handleTextFieldSave={handleTextFieldSave}
                          //@ts-ignore
                          handleSelectFieldSave={handleSelectFieldSave}
                          isEditable={allowModification}
                          hideEmptyFields
                          showEmptyFieldsButton={false}
                        />
                      )
                    )
                  ) : (
                    showCustomFields &&
                    (loading ? (
                      <div className="w-full flex items-center justify-center">
                        <ContentLoading />
                      </div>
                    ) : (
                      <CustomFieldsAccordion
                        className="edit-part-custom-fields-accordion"
                        numberFieldDropdownClassName="property-userDropdownMenu-edit-part-number"
                        dateFieldDropdownClassName="property-userDropdownMenu-edit-part-date"
                        textFieldDropdownClassName="property-userDropdownMenu-edit-part-text"
                        selectFieldClassName="edit-part-custom-select-field"
                        customFieldList={customFieldList}
                        //@ts-ignore
                        handleTextFieldSave={handleTextFieldSave}
                        //@ts-ignore
                        handleSelectFieldSave={handleSelectFieldSave}
                        isEditable={allowModification}
                        hideEmptyFields
                        showEmptyFieldsButton
                      />
                    ))
                  )}
                </div>
              </>
            ),
          },
          {
            id: "part-documentation",
            label: messages?.inventory["documentation"],
            content: (
              <div className="inventory-activity-log-container">
                <>
                  {loading && <ContentLoading />}
                  {!loading && inventoryPart && (
                    <PartDocuments
                      isSharedAssetsPage={isSharedAssetsPage}
                      userFolderToken={
                        inventoryPart?.accessToken || foldersAccessToken
                      }
                      part={inventoryPart}
                      allowModification={false}
                    />
                  )}
                </>
              </div>
            ),
          },
        ]}
      />
    </FormSheet>
  );
};

export default PartPreview;
